import { useMemo, useState, useEffect } from "react";
import { fetchBrand, fetchBrandKeys } from "../api";
import { usePolledData } from "../hooks/usePolledData";
import { fmtDate, fmtMs, fmtNum, fmtPct } from "../format";
import { KpiCard } from "../components/KpiCard";
import { Section } from "../components/Section";
import { DataTable, type ColumnDef } from "../components/DataTable";
import { ApiTypeTag, StatusBadge } from "../components/Badges";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend 
} from 'recharts';
import type {
  BrandApiRow,
  BrandRecentErrorRow,
  BrandResponse,
  BrandTopPanRow,
  TimeWindow,
  BrandRecord,
} from "../types";

interface Props {
  window: TimeWindow;
  brandId: string | null;
  onSelectBrand: (brandId: string) => void;
  registerState: (state: {
    loading: boolean;
    lastUpdated: Date | null;
    refetch: () => void;
  }) => void;
}

export const BrandPage = ({
  window,
  brandId,
  onSelectBrand,
  registerState,
}: Props) => {
  const [brands, setBrands] = useState<BrandRecord[]>([]);
  const [selectedApiType, setSelectedApiType] = useState<string>("ALL");

  useEffect(() => {
    fetchBrandKeys().then(res => setBrands(res.brands)).catch(console.error);
  }, []);

  const { data, error, loading, lastUpdated, refetch } = usePolledData<BrandResponse>(
    () => fetchBrand(brandId!, window),
    { enabled: !!brandId, intervalMs: 15_000, deps: [brandId, window] },
  );

  registerState({ loading, lastUpdated, refetch });

  const filteredApiRows = useMemo(() => {
    if (!data) return [];
    if (selectedApiType === "ALL") return data.by_api_type;
    return data.by_api_type.filter(r => r._id === selectedApiType);
  }, [data, selectedApiType]);

  const filteredPanRows = useMemo(() => {
    if (!data) return [];
    if (selectedApiType === "ALL") return data.top_pans;
    return data.top_pans.filter(r => r._id.api_type === selectedApiType);
  }, [data, selectedApiType]);

  const apiCols = useMemo<ColumnDef<BrandApiRow>[]>(
    () => [
      { key: "api", header: "API", render: (r) => <ApiTypeTag type={r._id} /> },
      { key: "total", header: "Hits", align: "right", render: (r) => fmtNum(r.total) },
      { key: "err", header: "Err", align: "right", render: (r) => fmtNum(r.errors) },
      {
        key: "cache",
        header: "Cache%",
        align: "right",
        render: (r) => fmtPct(r.total ? (r.cache_hits / r.total) * 100 : 0),
      },
      {
        key: "avg",
        header: "Avg",
        align: "right",
        render: (r) => fmtMs(r.avg_response_time_ms),
      },
    ],
    [],
  );

  const panCols = useMemo<ColumnDef<BrandTopPanRow>[]>(
    () => [
      { key: "pan", header: "PAN", render: (r) => r._id.pan },
      {
        key: "api",
        header: "API",
        render: (r) => <ApiTypeTag type={r._id.api_type} />,
      },
      { key: "count", header: "Hits", align: "right", render: (r) => fmtNum(r.count) },
      {
        key: "cache",
        header: "Cache%",
        align: "right",
        render: (r) => fmtPct(r.count ? (r.cache_hits / r.count) * 100 : 0),
      },
      { key: "last", header: "Last seen", render: (r) => fmtDate(r.last_seen) },
    ],
    [],
  );

  const errCols = useMemo<ColumnDef<BrandRecentErrorRow>[]>(
    () => [
      { key: "time", header: "Time", render: (r) => fmtDate(r.created_at) },
      { key: "api", header: "API", render: (r) => <ApiTypeTag type={r.api_type} /> },
      {
        key: "status",
        header: "Status",
        render: (r) => <StatusBadge code={r.response_status} />,
      },
      { key: "code", header: "Code", render: (r) => r.error_code || "—" },
      {
        key: "msg",
        header: "Message",
        render: (r) => (
          <span className="dim small clamp-2">{r.error_message || "—"}</span>
        ),
      },
      { key: "pan", header: "PAN", render: (r) => r.pan || "—" },
    ],
    [],
  );

  const chartData = useMemo(() => {
    if (!data) return [];
    return data.by_api_type.map(r => ({
      name: r._id,
      success: r.total - r.errors,
      errors: r.errors,
      cache: r.cache_hits
    }));
  }, [data]);

  return (
    <>
      <Section
        title="Brand Explorer"
        description="Select a brand and optionally an API type to dive into the logs."
      >
        <div className="row gap-12">
          <div className="field" style={{ flex: 1 }}>
            <label>Select Brand</label>
            <select 
              className="input" 
              value={brandId || ""} 
              onChange={e => onSelectBrand(e.target.value)}
            >
              <option value="" disabled>Choose a brand...</option>
              {brands.map(b => (
                <option key={b._id} value={b.brand_id}>{b.brand_name} ({b.brand_id})</option>
              ))}
            </select>
          </div>
          <div className="field" style={{ width: 180 }}>
            <label>API Type</label>
            <select 
              className="input" 
              value={selectedApiType} 
              onChange={e => setSelectedApiType(e.target.value)}
            >
              <option value="ALL">All APIs</option>
              <option value="PAN">PAN</option>
              <option value="CRIF">CRIF</option>
              <option value="UAN">UAN</option>
            </select>
          </div>
        </div>
      </Section>

      {!brandId && (
        <div className="info-banner grid-margin">
          Select a brand from the dropdown above to start exploring data.
        </div>
      )}

      {brandId && error && (
        <div className="error-banner">
          <strong>Failed to load brand stats:</strong> {error.message}
        </div>
      )}

      {brandId && data && (
        <>
          <Section
            title={`Performance Overview · ${brandId}`}
            description={`Stats for ${selectedApiType === 'ALL' ? 'All APIs' : selectedApiType} over ${window}`}
          >
            <div className="grid grid-4">
              <KpiCard
                label="Total Requests"
                value={fmtNum(selectedApiType === 'ALL' ? data.totals.total : filteredApiRows[0]?.total || 0)}
                sub={`upstream: ${fmtNum(selectedApiType === 'ALL' ? data.totals.upstream_calls : (filteredApiRows[0]?.total || 0) - (filteredApiRows[0]?.cache_hits || 0))}`}
              />
              <KpiCard
                label="Success Rate"
                tone="good"
                value={fmtPct(selectedApiType === 'ALL' ? data.rates.success_pct : (filteredApiRows[0]?.total ? ((filteredApiRows[0].total - filteredApiRows[0].errors) / filteredApiRows[0].total) * 100 : 0), 2)}
              />
              <KpiCard
                label="Error Rate"
                tone={(selectedApiType === 'ALL' ? data.rates.error_pct : filteredApiRows[0]?.errors) ? "bad" : "default"}
                value={fmtPct(selectedApiType === 'ALL' ? data.rates.error_pct : (filteredApiRows[0]?.total ? (filteredApiRows[0].errors / filteredApiRows[0].total) * 100 : 0), 2)}
              />
              <KpiCard
                label="Cache Hit Rate"
                tone="info"
                value={fmtPct(selectedApiType === 'ALL' ? data.rates.cache_hit_pct : (filteredApiRows[0]?.total ? (filteredApiRows[0].cache_hits / filteredApiRows[0].total) * 100 : 0), 2)}
              />
            </div>
          </Section>

          <div className="grid grid-margin">
             <Section title="API Performance Breakdown">
               <div style={{ width: '100%', height: 350, background: 'var(--bg-2)', borderRadius: 'var(--radius)', padding: '20px' }}>
                 <ResponsiveContainer>
                   <BarChart data={chartData}>
                     <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
                     <XAxis dataKey="name" stroke="var(--fg-dim)" />
                     <YAxis stroke="var(--fg-dim)" />
                     <Tooltip contentStyle={{ background: 'var(--bg-3)', border: '1px solid var(--line)', color: 'var(--fg)' }} />
                     <Legend />
                     <Bar dataKey="success" stackId="a" fill="var(--green)" radius={[0, 0, 0, 0]} />
                     <Bar dataKey="errors" stackId="a" fill="var(--red)" radius={[4, 4, 0, 0]} />
                     <Bar dataKey="cache" fill="var(--cyan)" radius={[4, 4, 0, 0]} />
                   </BarChart>
                 </ResponsiveContainer>
               </div>
             </Section>
          </div>

          <Section title="Per-API metrics">
            <DataTable
              columns={apiCols}
              rows={filteredApiRows}
              rowKey={(r) => String(r._id)}
              loading={loading}
            />
          </Section>

          <Section title="Top requested identifiers">
            <DataTable
              columns={panCols}
              rows={filteredPanRows}
              rowKey={(r) => `${r._id.pan}|${r._id.api_type}`}
              loading={loading}
            />
          </Section>

          <Section title="Diagnostic logs (Recent Errors)">
            <DataTable
              columns={errCols}
              rows={data?.recent_errors.filter(e => selectedApiType === 'ALL' || e.api_type === selectedApiType)}
              rowKey={(r) => r._id}
              loading={loading}
              emptyMessage="No errors found in this selection. ✨"
            />
          </Section>
        </>
      )}
    </>
  );
};
